// ============================================================================
// SignScheduleDialog.tsx — Signer (valider) son planning d'un mois.
// ----------------------------------------------------------------------------
// Affiche un résumé du mois (nb de créneaux + total d'heures), une zone de
// signature manuscrite et un champ « réserve » optionnel. Insère la signature
// dans planning.schedule_signatures (RLS self-insert). Réutilisé par la page
// « Mon planning » et par le contrôle à la connexion.
// ============================================================================
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { Loader2, PenLine, CalendarClock } from 'lucide-react';
import { SignaturePad } from './SignaturePad';
import { monthKey, monthLabel, monthBounds } from './schedule-signature';

/** Durée d'un shift en heures (gère un éventuel passage minuit improbable ici). */
function shiftHours(start: string, end: string): number {
  const [sh = 0, sm = 0] = start.split(':').map(Number);
  const [eh = 0, em = 0] = end.split(':').map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;
  return mins / 60;
}

export function SignScheduleDialog({ month, userId, signerName, onClose, onSigned }: {
  month: Date;
  userId: string;
  signerName: string;
  onClose: () => void;
  onSigned: () => void;
}) {
  const [summary, setSummary] = useState<{ count: number; hours: number } | null>(null);
  const [signature, setSignature] = useState<string | null>(null);
  const [reserve, setReserve] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Résumé du mois : nb de créneaux publiés + total d'heures.
  useEffect(() => {
    void (async () => {
      const { from, to } = monthBounds(month);
      const { data } = await supabase
        .schema('planning')
        .from('shifts')
        .select('start_time, end_time')
        .eq('user_id', userId)
        .eq('status', 'published')
        .gte('date', from)
        .lte('date', to);
      const rows = (data as { start_time: string; end_time: string }[]) ?? [];
      const hours = rows.reduce((s, r) => s + shiftHours(r.start_time, r.end_time), 0);
      setSummary({ count: rows.length, hours: Math.round(hours * 10) / 10 });
    })();
  }, [month, userId]);

  async function sign() {
    if (!signature) { setError('Merci de dessiner ta signature.'); return; }
    setSaving(true); setError(null);
    // Tables planning.* pas encore dans les types générés (migration à appliquer)
    // → client non typé pour l'écriture.
    const pdb = supabase.schema('planning') as unknown as { from: (t: string) => { insert: (v: unknown) => Promise<{ error: { message: string } | null }> } };
    const { error: err } = await pdb
      .from('schedule_signatures')
      .insert({
        user_id: userId,
        period_month: monthKey(month),
        signer_name: signerName,
        signature_data: signature,
        reserve_comment: reserve.trim() || null,
      });
    setSaving(false);
    if (err) { setError(err.message); return; }
    onSigned();
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenLine className="h-5 w-5 text-primary" />
            Signer mon planning — {monthLabel(month)}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center gap-2 rounded-md border bg-muted/40 p-2 text-sm">
            <CalendarClock className="h-4 w-4 text-muted-foreground" />
            {summary
              ? <span><strong>{summary.count}</strong> créneau{summary.count > 1 ? 'x' : ''} · <strong>{summary.hours} h</strong> au total</span>
              : <span className="text-muted-foreground">Chargement du récapitulatif…</span>}
          </div>

          <p className="text-xs text-muted-foreground">
            En signant, tu confirmes avoir pris connaissance de ton planning de <strong>{monthLabel(month)}</strong>.
            Une réserve n'empêche pas la validation — elle signale simplement un point à vérifier.
          </p>

          <div className="space-y-1">
            <Label>Ta signature</Label>
            <SignaturePad onChange={setSignature} />
          </div>

          <div className="space-y-1">
            <Label htmlFor="reserve">Réserve (optionnel)</Label>
            <textarea
              id="reserve"
              value={reserve}
              onChange={(e) => setReserve(e.target.value)}
              rows={2}
              placeholder="Ex : le créneau du 14 ne correspond pas à ce qui était convenu."
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Plus tard</Button>
          <Button onClick={() => void sign()} disabled={saving || !signature}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PenLine className="mr-2 h-4 w-4" />}
            Signer {monthLabel(month)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
